import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useEffect } from "react";
import {
  LayoutDashboard, Package, Activity, CreditCard, FileText,
  Bell, Users, Settings, LogOut, ChevronLeft, Menu, X, TrendingUp, BarChart3,
  Store, HelpCircle, Sun, Moon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useFeatures } from "@/lib/features";

export default function DashboardLayout() {
  const { user, logout, isAdmin } = useAuth();
  const { t } = useI18n();
  const { isEnabled } = useFeatures();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("sp_theme") !== "light");

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    return () => document.documentElement.classList.remove("dark");
  }, [darkMode]);

  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("sp_theme", newMode ? "dark" : "light");
  };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard"), end: true },
    { to: "/dashboard/products", icon: Package, label: t("nav.products") },
    { to: "/dashboard/shops", icon: Store, label: "Webshops" },
    { to: "/dashboard/repricing", icon: TrendingUp, label: "Repricing" },
    { to: "/dashboard/analytics", icon: BarChart3, label: t("nav.analytics") },
    { to: "/dashboard/monitoring", icon: Activity, label: t("nav.monitoring") },
    { to: "/dashboard/subscription", icon: CreditCard, label: t("nav.subscription") },
    { to: "/dashboard/invoices", icon: FileText, label: t("nav.invoices") },
    { to: "/dashboard/alerts", icon: Bell, label: t("nav.alerts") },
    { to: "/dashboard/affiliate", icon: Users, label: t("nav.affiliate") },
    { to: "/dashboard/faq", icon: HelpCircle, label: t("nav.faq") },
    { to: "/dashboard/settings", icon: Settings, label: t("nav.settings") },
  ].filter((item) => item.to !== "/dashboard/affiliate" || isEnabled("affiliate_module"));

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[240px] bg-card border-r border-border flex flex-col transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-border">
          <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <img src="/favicon.png" alt="Smartpriced" className="w-8 h-8 rounded-md" />
            <span className="font-bold text-lg tracking-tight">Smartpriced</span>
          </NavLink>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                  isActive ? "active" : "text-muted-foreground hover:text-foreground"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="pt-4 pb-2 px-3">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin</span>
              </div>
              <NavLink
                to="/admin"
                className="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground"
                data-testid="nav-admin-panel"
                onClick={() => setSidebarOpen(false)}
              >
                <Settings className="w-4 h-4" />
                {t("nav.admin_panel")}
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-border flex items-center justify-between px-4 lg:px-6 bg-card/50 backdrop-blur-sm sticky top-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="h-8 w-8">
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <LanguageSwitcher />
            {user?.subscription?.status && (
              <Badge variant={user.subscription.status === "trial" ? "secondary" : "default"} data-testid="subscription-badge">
                {user.subscription.plan_name} ({user.subscription.status})
              </Badge>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
